import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import type { ImportJobStatus } from '@/lib/utils'

/**
 * Read-only store for gabellotto import jobs data.
 * Separate from admin.ts because this is monitoring data, not CRUD operations.
 */

export type RunMethodInfo = {
  method: string
  status: string
}

export type DataImportJob = {
  id: string
  brand: string | null
  brand_id: string | null
  brand_slug: string | null
  platform: string
  platform_advertiser_identifier: string | null
  country_code: string | null
  date_from: string | null
  date_to: string | null
  method: string | null
  status: ImportJobStatus
  scheduled_ts: string | null
  started_ts: string | null
  completed_ts: string | null
  num_ads: number | null
  error_message: string | null
  apify_platform_run_id: string | null
  apify_platform_dataset_id: string | null
  cloud_run_job_id: string | null
  cloud_run_execution_id: string | null
  task_ix: number | null
  extra_kwargs: Record<string, unknown> | null
  failure_cnt: number | null
  batch_id: string | null
  gcs_uri: string | null
  successful_run_id: string | null
  run_methods: RunMethodInfo[]
  scraping_url: string | null
}

export type DataImportJobRun = {
  id: string
  status: ImportJobStatus
  method: string | null
  started_ts: string | null
  completed_ts: string | null
  num_ads: number | null
  error_message: string | null
  cloud_run_job_id: string | null
  cloud_run_execution_id: string | null
  task_ix: number | null
  apify_platform_run_id: string | null
  apify_platform_dataset_id: string | null
}

export type DataImportJobDetail = DataImportJob & {
  job_runs: DataImportJobRun[]
}

export type DataImportJobsResponse = {
  items: DataImportJob[]
  total: number
  page: number
  page_size: number
}

export type ImportJobsQuery = {
  page?: number
  page_size?: number
  search?: string
  platforms?: string[]
  methods?: string[]
  job_statuses?: ImportJobStatus[]
  countries?: string[]
  sectors?: string[]
  clients?: string[]
  completed_op?: string
  completed_date?: string
  completed_date_end?: string
  started_op?: string
  started_date?: string
  started_date_end?: string
  data_range_from?: string
  data_range_to?: string
  num_ads?: 'has_ads' | 'no_ads'
  sort_by?: string
  sort_desc?: boolean
}

export type PlatformImportStats = {
  platformCode: string
  latestTs: string | null
  latestStatus: ImportJobStatus | null
  lastSuccessfulTs: string | null
  lastWithAdsTs: string | null
  lastAdsCount: number | null
}

export type AdvertiserImportStats = {
  platformCode: string
  platformAdvertiserIdentifier: string
  countryCode: string | null
  status: ImportJobStatus
  completedTs: string | null
  numAds: number | null
  jobId: string | null
}

type BrandImportStatsRaw = {
  brand_id: string
  platform_code: string
  latest_ts: string | null
  latest_status: ImportJobStatus | null
  last_successful_ts: string | null
  last_with_ads_ts: string | null
  last_ads_count: number | null
}

type AdvertiserImportStatsRaw = {
  platform_code: string
  platform_advertiser_identifier: string
  country_code: string | null
  status: ImportJobStatus
  completed_ts: string | null
  num_ads: number | null
  job_id: string | null
}

type ErrorResponse = { detail: string }

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || ''

export const useImports = defineStore('imports', () => {
  const auth = useAuthStore()

  const importJobs = ref<DataImportJob[]>([])
  const importJobsTotal = ref(0)
  const importJobsPage = ref(1)
  const importJobsPageSize = ref(50)

  // Track latest request to discard stale responses from superseded requests
  let fetchRequestId = 0

  async function fetchImportJobs(query: ImportJobsQuery = {}): Promise<DataImportJobsResponse> {
    const requestId = ++fetchRequestId

    const params = new URLSearchParams()
    if (query.page != null) params.set('page', String(query.page))
    if (query.page_size != null) params.set('page_size', String(query.page_size))
    if (query.search) params.set('search', query.search)
    if (query.platforms?.length) {
      for (const p of query.platforms) params.append('platform', p)
    }
    if (query.methods?.length) {
      for (const m of query.methods) params.append('method', m)
    }
    if (query.job_statuses?.length) {
      for (const s of query.job_statuses) params.append('job_status', s)
    }
    if (query.countries?.length) {
      for (const c of query.countries) params.append('country', c)
    }
    if (query.sectors?.length) {
      for (const s of query.sectors) params.append('sector', s)
    }
    if (query.clients?.length) {
      for (const c of query.clients) params.append('client', c)
    }
    if (query.completed_op && query.completed_date) {
      params.set('completed_op', query.completed_op)
      params.set('completed_date', query.completed_date)
      if (query.completed_date_end) params.set('completed_date_end', query.completed_date_end)
    }
    if (query.started_op && query.started_date) {
      params.set('started_op', query.started_op)
      params.set('started_date', query.started_date)
      if (query.started_date_end) params.set('started_date_end', query.started_date_end)
    }
    if (query.data_range_from) params.set('data_range_from', query.data_range_from)
    if (query.data_range_to) params.set('data_range_to', query.data_range_to)
    if (query.num_ads) params.set('num_ads', query.num_ads)
    if (query.sort_by) params.set('sort_by', query.sort_by)
    if (query.sort_desc != null) params.set('sort_desc', String(query.sort_desc))

    const response = await fetch(`${BACKEND_URL}/api/admin/imports?${params}`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${auth.stytchToken}` },
    })
    if (!response.ok) {
      let detail = 'Error fetching imports'
      try {
        const err = await response.json()
        const maybeDetail = (err as ErrorResponse)?.detail
        if (typeof maybeDetail === 'string') detail = maybeDetail
      } catch {}
      throw new Error(detail)
    }
    const result = await response.json()
    if (!Array.isArray(result?.items) || typeof result?.total !== 'number') {
      throw new Error('Unexpected response shape')
    }
    // Discard stale response if a newer request was made
    if (requestId !== fetchRequestId) {
      return result
    }
    importJobs.value = result.items
    importJobsTotal.value = result.total
    importJobsPage.value = result.page
    importJobsPageSize.value = result.page_size
    return result
  }

  async function fetchImportJobDetail(importId: string): Promise<DataImportJobDetail> {
    const response = await fetch(`${BACKEND_URL}/api/admin/imports/${importId}`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${auth.stytchToken}` },
    })
    if (!response.ok) {
      let detail = 'Error fetching import detail'
      try {
        const err = await response.json()
        const maybeDetail = (err as ErrorResponse)?.detail
        if (typeof maybeDetail === 'string') detail = maybeDetail
      } catch {}
      throw new Error(detail)
    }
    const result = await response.json()
    if (
      !result?.id ||
      typeof result.status !== 'string' ||
      typeof result.platform !== 'string' ||
      !Array.isArray(result?.job_runs)
    ) {
      throw new Error('Unexpected response shape')
    }
    return result as DataImportJobDetail
  }

  async function fetchAllBrandImportStats(): Promise<Map<string, PlatformImportStats[]>> {
    const response = await fetch(`${BACKEND_URL}/api/admin/imports/stats/all`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch brand import stats')
    }
    const data = await response.json()
    if (!Array.isArray(data)) {
      throw new Error('Unexpected response shape from import stats endpoint')
    }
    const map = new Map<string, PlatformImportStats[]>()
    for (const item of data as BrandImportStatsRaw[]) {
      if (!map.has(item.brand_id)) {
        map.set(item.brand_id, [])
      }
      map.get(item.brand_id)!.push({
        platformCode: item.platform_code,
        latestTs: item.latest_ts,
        latestStatus: item.latest_status,
        lastSuccessfulTs: item.last_successful_ts,
        lastWithAdsTs: item.last_with_ads_ts,
        lastAdsCount: item.last_ads_count,
      })
    }
    return map
  }

  async function fetchBrandAdvertiserImportStats(
    brandId: string,
  ): Promise<AdvertiserImportStats[]> {
    const response = await fetch(`${BACKEND_URL}/api/admin/brands/${brandId}/import-stats`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch advertiser import stats')
    }
    const data = await response.json()
    if (!Array.isArray(data)) {
      throw new Error('Unexpected response shape from advertiser import stats endpoint')
    }
    return (data as AdvertiserImportStatsRaw[]).map((item) => ({
      platformCode: item.platform_code,
      platformAdvertiserIdentifier: item.platform_advertiser_identifier,
      countryCode: item.country_code,
      status: item.status,
      completedTs: item.completed_ts,
      numAds: item.num_ads,
      jobId: item.job_id,
    }))
  }

  return {
    importJobs,
    importJobsTotal,
    importJobsPage,
    importJobsPageSize,
    fetchImportJobs,
    fetchImportJobDetail,
    fetchAllBrandImportStats,
    fetchBrandAdvertiserImportStats,
  }
})
